// Import de la nomenclature budgétaire (lot L4-B, D-140) : fichier CSV choisi sur le poste (ou collé),
// aperçu analysé par la même fonction que le serveur (analyserCsvNomenclature), import tout ou rien sur
// un budget en préparation. Aucune nomenclature officielle n'est préchargée (à confirmer par
// l'administration) : le fichier vient de la collectivité.
import { analyserCsvNomenclature, COLONNES_CSV_NOMENCLATURE, SECTIONS_BUDGET, SENS_LIGNE_BUDGET, type CreationLigneBudget } from '@cc/contrats';
import { Bandeau, Bouton, EtatVide, Selection, Tableau, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { FileUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { useAction } from '../../commun/useAction';
import { importerNomenclature, INVALIDER_BUDGET, useBudgets } from './api';
import { francs } from './logique';
import { NavigationBudget } from './NavigationBudget';
import styles from './budget.module.css';

const COLONNES: ColonneTableau<CreationLigneBudget & { n: number }>[] = [
  { id: 'section', entete: 'Section', cellule: (l) => SECTIONS_BUDGET[l.section] },
  { id: 'sens', entete: 'Sens', cellule: (l) => SENS_LIGNE_BUDGET[l.sens] },
  { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (l) => <span className="cc-mono">{l.code}</span> },
  { id: 'libelle', entete: 'Libellé', cellule: (l) => l.libelle },
  { id: 'prevu', entete: 'Prévu', numerique: true, cellule: (l) => francs(l.prevu) },
];

export function PageImport() {
  const [parametres] = useSearchParams();
  const navigate = useNavigate();
  const budgets = useBudgets();
  const enPreparation = (budgets.data?.elements ?? []).filter((b) => b.etat === 'en_preparation');
  const [budgetId, setBudgetId] = useState(parametres.get('budget') ?? '');
  const choisi = budgetId || enPreparation[0]?.id || '';
  const [csv, setCsv] = useState('');
  const analyse = useMemo(() => (csv.trim() ? analyserCsvNomenclature(csv) : null), [csv]);
  const action = useAction({
    executer: () => importerNomenclature(choisi, csv),
    succes: (r) => `${r.importees} ligne(s) importée(s), ${francs(r.total_prevu)} prévus.`,
    invalider: INVALIDER_BUDGET,
    apres: () => navigate('/budget'),
  });
  const lireFichier = (f: File | undefined) => {
    if (f) void f.text().then(setCsv);
  };
  return (
    <>
      <EnTetePage etiquette="Finances et budget" titre="Import de la nomenclature" description={`Fichier CSV avec une ligne d'en-tête : ${COLONNES_CSV_NOMENCLATURE.join(', ')} (chapitre, article et paragraphe facultatifs), séparateur point-virgule ou virgule. Tout ou rien.`} />
      <NavigationBudget actif="import" />
      {enPreparation.length === 0 ? (
        <EtatVide titre="Aucun budget en préparation" icone={FileUp}><p>L&apos;import se fait sur un budget en préparation ; après l&apos;adoption, les crédits changent par décision modificative.</p></EtatVide>
      ) : (
        <section className={styles.bloc}>
          <Selection libelle="Budget" options={enPreparation.map((b) => ({ valeur: b.id, libelle: `${b.exercice} · ${b.libelle}` }))} value={choisi} onChange={(e) => setBudgetId(e.target.value)} />
          <label className={styles.fichier}>
            Fichier CSV
            <input type="file" accept=".csv,text/csv" onChange={(e) => lireFichier(e.target.files?.[0])} />
          </label>
          <ZoneTexte libelle="Contenu du fichier" value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} />
          {analyse && analyse.erreurs.length > 0 ? (
            <Bandeau type="erreur">
              {analyse.erreurs.length} erreur(s) : {analyse.erreurs.slice(0, 8).map((e) => `ligne ${e.ligne} : ${e.message}`).join(' ; ')}
            </Bandeau>
          ) : null}
          {analyse && analyse.lignes.length > 0 ? (
            <>
              <p className={styles.note}>{analyse.lignes.length} ligne(s) lue(s), {francs(analyse.lignes.reduce((s, l) => s + l.prevu, 0))} prévus au total.</p>
              <Tableau legende="Aperçu de l'import" colonnes={COLONNES} lignes={analyse.lignes.map((l, n) => ({ ...l, n }))} cleLigne={(l) => String(l.n)} cleColonnes="budget-import" />
            </>
          ) : null}
          <Bouton variante="principal" icone={FileUp} chargement={action.enCours} disabled={!analyse || analyse.erreurs.length > 0 || analyse.lignes.length === 0} onClick={() => action.lancer(undefined)}>
            Importer les lignes
          </Bouton>
          {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
        </section>
      )}
    </>
  );
}
